import React, { useState } from "react";
import * as XLSX from "xlsx";
import CryptoJS from "crypto-js";
import {
  setDoc,
  doc,
  serverTimestamp,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { Button, Typography, CircularProgress, Box } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import "./AdminPage.css";
import { db } from "../firebaseConfig";

const AdminPage = () => {
  const [processing, setProcessing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [progress, setProgress] = useState(0);

  const generateScreenId = (userId) => {
    const hash = CryptoJS.SHA256(userId + "tozos").toString();
    return `${userId}tozos${hash.substring(0, 5)}`;
  };
  const generateScreenIdOyun = (userId) => {
    const hash = CryptoJS.SHA256(userId + "oyun").toString();
    return `${userId}oyun${hash.substring(0, 5)}`;
  };
  const processRow = async (row) => {
    const {
      user_id: userId,
      document_type: documentType,
      document_id: existingDocId,
      school_name: schoolName = "",
      user_name: userName = "",
      city = "",
      district = "",
      organizer_institution = "",
      document_date = "",
      document_number = "",
      event_start_date = "",
      event_end_date = "",
      event_city = "",
      event_district = "",
      event_location = "",
      event_name = "",
      representation_level_province = "",
      representation_level_area = "",
      representation_level_national = "",
      representation_level_international = "",
      event_level_performance = "",
      document_number_two = "",
      start_date_two = "",
      event_level_participation = "",
      degree = "",
    } = row;

    if (!userId || !documentType) {
      return null;
    }

    let screenIdGenerator = generateScreenId;
    if (documentType && documentType.toLowerCase().includes("oyun")) {
      screenIdGenerator = generateScreenIdOyun;
    }

    try {
      const isIBBDocument = existingDocId?.toString()?.trim()?.toLowerCase()?.endsWith("ibb") ?? false;
      
      const hashedValue = existingDocId || CryptoJS.SHA256(userId + documentType).toString();

      const docRef = doc(db, "documents", hashedValue.toString());
      const existingDoc = await getDoc(docRef);

      if (existingDoc.exists()) {
        return null;
      }

      const documentData = isIBBDocument ? {
        user_id: String(userId),
        document_type: documentType,
        organizer_institution,
        document_date,
        document_number,
        event_start_date,
        event_end_date,
        event_city,
        event_district,
        event_location,
        event_name,
        representation_level_province,
        representation_level_area,
        representation_level_national,
        representation_level_international,
        event_level_performance,
        user_name: userName,
        document_number_two,
        start_date_two,
        event_level_participation,
        screen_id: screenIdGenerator(userId),
        created_at: serverTimestamp(),
        degree // degree ekleniyor
      } : {
        user_id: String(userId),
        document_type: documentType,
        school_name: schoolName,
        user_name: userName,
        city,
        district,
        screen_id: screenIdGenerator(userId),
        created_at: serverTimestamp(),
        degree // degree ekleniyor
      };

      
      try {
        await setDoc(doc(db, "documents", hashedValue.toString()), documentData);
      } catch (error) {
        console.error("setDoc hatası:", error);
        throw error;
      }

      if (!isIBBDocument && screenIdGenerator(userId)) {
        const screenRef = doc(
          db,
          "screens",
          screenIdGenerator(userId).toString()
        );
        const screenDoc = await getDoc(screenRef);

        let documentsIds = [];

        if (screenDoc.exists()) {
          documentsIds = screenDoc.data().documents_ids || [];
        }

        documentsIds.push(hashedValue);

        await setDoc(
          screenRef,
          {
            user_id: String(userId),
            documents_ids: documentsIds,
          },
          { merge: true }
        );
      }

      return { ...row, screen_id: screenIdGenerator(userId) };
    } catch (error) {
      console.error("Belge işlenirken hata:", error);
      return null;
    }
  };

  const processFile = async (jsonData) => {
    const totalRows = jsonData.length;
    const enrichedData = [];
    let skippedCount = 0;

    for (let i = 0; i < totalRows; i++) {
      const row = await processRow(jsonData[i]);
      if (row) {
        enrichedData.push(row);
      } else {
        skippedCount++;
      }

      const currentProgress = (((i + 1) / totalRows) * 100).toFixed(2);
      setProgress(currentProgress);
      setStatusMessage(
        `İlerleme: ${i + 1}/${totalRows} (%${currentProgress}) - Atlanan: ${skippedCount}`
      );
    }

    return enrichedData;
  };

  const exportToExcel = (data, fileName) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Veriler");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setStatusMessage("Lütfen bir dosya yükleyin.");
      return;
    }

    setProcessing(true);
    setCompleted(false);
    setStatusMessage("Dosya işleniyor...");

    const reader = new FileReader();
    reader.onload = async (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const enrichedData = await processFile(jsonData);

      exportToExcel(enrichedData, "Yarışmacı_Belgeleri.xlsx");
      setProcessing(false);
      setCompleted(true);
      setStatusMessage(
        "Tüm veriler yüklendi ve screen_id oluşan excel dosyası indirildi."
      );
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <div className="centered-container">
      <Box className="admin-page-container">
        <UploadFileIcon className="upload-icon" />
        <Typography variant="h4" gutterBottom className="header-text">
          Excel Dosyası Yükle
        </Typography>

        <Button
          component="label"
          sx={{
            backgroundColor: "var(--color-primary-main)",
            color: "#fff",
            borderRadius: "8px",
            padding: "12px 24px",
            textTransform: "none",
            fontSize: "16px",
            "&:hover": {
              backgroundColor: "#660066",
              transform: "scale(1.05)",
            },
            mb: 2,
          }}
        >
          Dosyayı Yükle
          <input
            type="file"
            hidden
            accept=".xlsx, .xls"
            onChange={handleFileChange}
          />
        </Button>

        {processing && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 2,
            }}
          >
            <CircularProgress color="primary" />
            <Typography variant="body1" sx={{ mt: 2 }}>
              {statusMessage}
            </Typography>
          </Box>
        )}
        {completed && (
          <Typography variant="body1" sx={{ mt: 2, color: "green" }}>
            {statusMessage}
          </Typography>
        )}
        {progress > 0 && (
          <Typography variant="body1" sx={{ mt: 1 }}>
            Yüklenme durumu: {progress} %
          </Typography>
        )}
      </Box>
      <Box sx={{ ml: 4 }}>
        <Typography variant="body1" color="textSecondary" className="info-text">
          Lütfen yüklediğiniz Excel dosyasında aşağıdaki sütunların olduğundan
          emin olun:
          <strong>
            user_id, document_type, school_name, user_name, city, district, <span style={{color:'#eeca31'}}>degree</span>
          </strong>
        </Typography>
      </Box>
    </div>
  );
};

export default AdminPage;
